import React, { useEffect, useMemo, useState } from "react";

/**
 * GreetingBanner
 * ------------------------------------------------------
 * A drop‑in React component that shows a time‑based greeting
 * (Good morning/afternoon/evening/night) with a matching image.
 *
 * Fixed size: width = 332px, height = 80px
 */
export default function GreetingBanner({
  name,
  images,
  className = "",
  refreshIntervalMs = 60_000,
}) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), refreshIntervalMs);
    return () => clearInterval(id);
  }, [refreshIntervalMs]);

  const { greeting, partOfDay } = useMemo(() => getGreeting(now), [now]);

  const defaultImages = {
    morning:
      "https://images.pexels.com/photos/209807/pexels-photo-209807.jpeg?cs=srgb&dl=pexels-pixabay-209807.jpg&fm=jpg", // sunrise
    afternoon:
      "https://images.unsplash.com/photo-1501785888041-af3ef285b470?q=80&w=1600&auto=format&fit=crop", // sunny day
    evening:
      "https://images.unsplash.com/photo-1649006775680-e642cabdf0df?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8ZXZlbmluZyUyMHN1bnxlbnwwfHwwfHx8MA%3D%3D", // golden hour
    night:
      "https://images.pexels.com/photos/355887/pexels-photo-355887.jpeg?cs=srgb&dl=pexels-pixabay-355887.jpg&fm=jpg", // night sky
  };

  const imgSrc = (images && images[partOfDay]) || defaultImages[partOfDay];

  return (
    <section
      className={`relative overflow-hidden rounded-lg shadow ${className}`}
      aria-label="Time-based greeting"
      style={{ width: "340px", height: "95px" }}
    >
      <img
        src={imgSrc}
        alt={`${partOfDay} background`}
        className="w-full h-full object-cover"
        loading="eager"
      />

      {/* gradient overlay for text readability */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />

      <div className="absolute inset-0 flex items-center justify-between px-3">
        <div>
          <h1 className="text-white text-sm font-semibold drop-shadow">
            {greeting}
            {name ? `, ${name}` : ""}
          </h1>
          <p className="text-white/90 text-xs mt-0.5">{formatTime(now)}</p>
        </div>

        {/* Optional: emoji that matches time of day */}
        <span className="text-lg select-none" aria-hidden>
          {iconFor(partOfDay)}
        </span>
      </div>
    </section>
  );
}

// Helpers -------------------------------------------------

function getGreeting(date) {
  const h = date.getHours(); // local time
  if (h >= 5 && h < 12) return { greeting: "Good morning", partOfDay: "morning" };
  if (h >= 12 && h < 17) return { greeting: "Good afternoon", partOfDay: "afternoon" };
  if (h >= 17 && h < 21) return { greeting: "Good evening", partOfDay: "evening" };
  return { greeting: "Good night", partOfDay: "night" };
}

function formatTime(d) {
  return d.toLocaleString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function iconFor(partOfDay) {
  switch (partOfDay) {
    case "morning":
      return "";
    case "afternoon":
      return "";
    case "evening":
      return "";
    case "night":
    default:
      return "";
  }
}
